/**
 * The frozen MCP tool contract (mcp-server-design §1). Tool names, arg names, output field
 * names are STABLE at v1 — additive evolution only. This module is the single source of the
 * 9-tool surface and the role→tool visibility matrix (§2.2). Handlers live in the API package
 * (the registry); the *shape* lives here because the AI-answering brain and future OpenAPI /
 * function-calling bindings project the same contract (§4.2).
 */
import type { Role } from '../constants/roles';

/** The 9 curated, question-shaped tools (8 core + whats_my_day). */
export const MCP_TOOL_NAMES = [
  'whats_my_day',
  'get_schedule',
  'get_job',
  'customer_360',
  'get_ar_aging',
  'list_payments',
  'get_agreements',
  'get_reconciliation_status',
  'get_history',
] as const;
export type McpToolName = (typeof MCP_TOOL_NAMES)[number];

/**
 * Role → visible tools (§2.2). `tools/list` is filtered by this (a tech's client never even
 * sees get_ar_aging), and the service guard refuses a call to an unlisted tool. Row scoping
 * (own-visits-only, money-field dropping) is a SEPARATE concern enforced in the read handlers.
 */
export const TOOL_VISIBILITY: Record<McpToolName, readonly Role[]> = {
  whats_my_day: ['owner', 'office', 'tech'],
  get_schedule: ['owner', 'office', 'tech'],
  get_job: ['owner', 'office', 'bookkeeper', 'tech'],
  customer_360: ['owner', 'office', 'bookkeeper', 'tech'],
  get_ar_aging: ['owner', 'office', 'bookkeeper'],
  list_payments: ['owner', 'office', 'bookkeeper'],
  get_agreements: ['owner', 'office'],
  get_reconciliation_status: ['owner', 'bookkeeper'],
  get_history: ['owner', 'office', 'bookkeeper', 'tech'],
};

export function toolVisibleToRole(tool: McpToolName, role: Role): boolean {
  return TOOL_VISIBILITY[tool].includes(role);
}

/** Tools a role can see, minus any org- or connection-disabled ones. */
export function visibleTools(
  role: Role,
  disabled: readonly string[] = [],
): McpToolName[] {
  const off = new Set(disabled);
  return MCP_TOOL_NAMES.filter((t) => toolVisibleToRole(t, role) && !off.has(t));
}
